/**
 * Quins agents de Miranda s'ofereixen al selector (giny lateral i `/chat`), i quin queda triat.
 *
 * Decisió d'en Pere (14-OCT-2026): la Miranda única (`miranda`) per defecte, i els agents vells
 * (`router`, `hotel`, `guests`...) fora del selector, sense esborrar-los. **El fre d'emergència** és
 * el de sempre: a producció `miranda` s'apaga amb la fila inactiva o amb `SINGLE_MIRANDA_DISABLED`,
 * i llavors `GET /agent/agents` no la torna. Sense `miranda` a la llista, el selector ensenya tots
 * els agents com abans i el valor per defecte torna a ser `router`: ningú es queda sense Miranda.
 *
 * Mentre la llista carrega (`agents === undefined`, també si la crida falla) no es decideix res.
 *
 * Aquí no hi ha res de React ni d'emmagatzematge: funcions pures, perquè es puguin provar soles.
 */

/** La Miranda única: el xat ric (`services/miranda-rich-chat.ts`, `RICH_CHAT_AGENT_ID`). */
export const SINGLE_MIRANDA_AGENT_ID = 'miranda';

/** El nom que es pinta si `miranda` ha de sortir al selector i la llista ja no la porta. */
export const SINGLE_MIRANDA_AGENT_NAME = 'Miranda única';

/** L'agent per defecte quan `miranda` és apagada (el fre): el d'abans. */
export const FALLBACK_AGENT_ID = 'router';

type AgentLike = { id: string; name?: string };

/** La llista de `GET /agent/agents`, o `undefined` mentre carrega. */
export type AgentList = readonly AgentLike[] | undefined;

export interface AgentOption {
  id: string;
  name: string;
}

/** `true` si la llista porta `miranda`, `false` si no (el fre), `null` mentre carrega. */
export function isSingleMirandaListed(agents: AgentList): boolean | null {
  if (!agents) return null;
  return agents.some((agent) => agent.id === SINGLE_MIRANDA_AGENT_ID);
}

/**
 * L'agent amb què es parla de debò. Només canvia una cosa: amb el fre posat, `miranda` passa a
 * `router`. Un agent vell es respecta sempre (una conversa vella oberta, o una tria feta amb el fre).
 */
export function effectiveAgentId(agentId: string, agents: AgentList): string {
  if (agentId === SINGLE_MIRANDA_AGENT_ID && isSingleMirandaListed(agents) === false) {
    return FALLBACK_AGENT_ID;
  }
  return agentId;
}

/**
 * L'agent d'una conversa nova a `/chat` («+» o esborrar l'oberta): `miranda` si hi és; amb el fre,
 * el que hi havia (o `router` en lloc de `miranda`); mentre carrega, el que hi havia.
 */
export function agentForNewConversation(agentId: string, agents: AgentList): string {
  if (isSingleMirandaListed(agents) === true) return SINGLE_MIRANDA_AGENT_ID;
  return effectiveAgentId(agentId, agents);
}

/**
 * Les opcions del selector. Amb `miranda` a la llista, només `miranda`; sense (el fre), totes.
 *
 * L'agent actual hi és **sempre**, encara que la llista no el porti (una conversa d'un agent vell
 * oberta a `/chat`, la sessió vella del giny fins a «Netejar», o la llista encara carregant): un
 * Radix Select amb un valor que no és a cap opció queda en blanc. Va al final, amb el seu nom si la
 * llista sencera el coneix.
 */
export function agentSelectorOptions(agents: AgentList, currentId: string): AgentOption[] {
  const list = agents ?? [];
  const offered =
    isSingleMirandaListed(agents) === true
      ? list.filter((agent) => agent.id === SINGLE_MIRANDA_AGENT_ID)
      : [...list];

  const options = offered.map((agent) => ({ id: agent.id, name: agent.name ?? agent.id }));
  if (!options.some((option) => option.id === currentId)) {
    const known = list.find((agent) => agent.id === currentId);
    options.push({
      id: currentId,
      name:
        known?.name ??
        (currentId === SINGLE_MIRANDA_AGENT_ID ? SINGLE_MIRANDA_AGENT_NAME : currentId),
    });
  }
  return options;
}

export interface StoredAgentState {
  /** L'agent del giny (el desat, o el per defecte). */
  agentId: string;
  /** Hi ha una conversa desada? És de l'agent desat: canviar d'agent la buida sempre. */
  hasSession: boolean;
  /** La marca de la migració d'una sola vegada. */
  migrated: boolean;
}

export interface StoredAgentStep {
  agentId: string;
  migrated: boolean;
}

/**
 * Què s'ha de fer amb l'agent desat del giny quan arriba la llista. `null`: res.
 *
 * - **Carregant**: res.
 * - **Amb `miranda`**, la migració d'una sola vegada: qui té un agent vell passa a `miranda` i es
 *   marca. Si té una conversa desada d'aquell agent, la continua amb el client de text i passa a
 *   `miranda` en quedar-se sense (Netejar, o la sessió que el backend ja no troba). Amb la marca
 *   posada, l'agent desat es respecta.
 * - **Sense `miranda`** (el fre): qui era a `miranda` passa a `router` i la marca es treu, perquè
 *   quan el fre es tregui la migració torni a passar una vegada. Un agent vell es respecta.
 */
export function nextStoredAgentStep(
  state: StoredAgentState,
  agents: AgentList,
): StoredAgentStep | null {
  const listed = isSingleMirandaListed(agents);
  if (listed === null) return null;

  if (listed === false) {
    if (state.agentId !== SINGLE_MIRANDA_AGENT_ID) return null;
    return { agentId: FALLBACK_AGENT_ID, migrated: false };
  }

  if (state.migrated) return null;
  if (state.agentId !== SINGLE_MIRANDA_AGENT_ID && state.hasSession) return null;
  return { agentId: SINGLE_MIRANDA_AGENT_ID, migrated: true };
}
